import Head from "next/head";
import Link from "next/link";
import Script from "next/script";

import { SSText } from "src/components/base/components/SSText";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { openPdfInNewTab } from "src/utils/utils";
import { global_config } from "src/shared/global_config";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

const SWBLink = () => (
  <Link target="_blank" href="https://am.ticketmaster.com/swbyankees/">
    Account Manager
  </Link>
);

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question:
      "How do I claim my tickets? When does the opt in open before each game?",
    answer:
      "You will receive a text when the window to check-in is open. The check-in window starts at 9 am on the first game day of a homestand. You will have an opportunity to check-in for all the games in the series.",
  },
  {
    question:
      "Do I get a ticket to each game on the schedule? Are there penalties if I do not check-in?",
    answer:
      "Tickets are guaranteed for each game on the schedule. There are no penalties for not attending a game, however you do need to confirm your attendance in order to receive a ticket.",
  },
  {
    question: "Where are my tickets?",
    answer: (
      <>
        Your tickets will be sent over 3 hours prior to game start and you can
        find the tickets within your RailRiders <SWBLink />.
      </>
    ),
  },
  {
    question: "How are tickets sent to me?",
    answer: (
      <>
        3 hours prior to game start you will receive a text confirming that
        tickets have been delivered to your RailRiders <SWBLink />.
      </>
    ),
  },
  {
    question: "Where will my seats be located?",
    answer: "Seat Locations are GA at this time.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "RailRiders All-Access April Pass tickets are not eligible for resale. Abusing these guidelines can result in your RailRiders All-Access April Pass being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed. Please contact info@swbrailriders.com  for any assistance you may require.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: (
      <>
        We do not issue any refunds for games you are not able to attend. If
        your game ticket qualifies for a Rain Out Exchange, please email{" "}
        <Link href="mailto:rainout@swbrailriders.com">
          rainout@swbrailriders.com
        </Link>{" "}
        to do so.
      </>
    ),
  },
  {
    question: "Can I opt out of my Monthly RailRiders All-Access Pass?",
    answer:
      "You will receive a text message when the opt out period for the month opens, typically on the 15th. Your card will be charged on the 26th of the month.",
  },
  {
    question:
      "How can I get a refund and cancel my RailRiders All-Access Pass?",
    answer:
      "Please contact info@swbrailriders.com for any requests for refunds or cancellations outside of the opt out window.",
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    I understand that I am agreeing to a monthly subscription through the rest
    of the season. It will renew at $24.99 per pass each month through September
    2025 unless I choose to cancel my subscription during the given opt-out
    period. I agree to receive SMS alerts regarding ticket delivery and agree to
    the{" "}
    <Link
      href={
        "https://cdn.prod.website-files.com/66b3d5184e88b53f24e71360/6836259e2aac5657874b9955_Attend%20Terms%20of%20Use%20052425.pdf"
      }
      target={"_blank"}
    >
      Terms & Conditions
    </Link>{" "}
    and{" "}
    <Link
      href={
        "https://cdn.prod.website-files.com/66b3d5184e88b53f24e71360/683625a812daf6ceadcbe532_Attend%20Privacy%20Policy%20052725.pdf"
      }
      target={"_blank"}
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;e all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <>
      <Head>
        <script
          defer
          nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
          id={`${TEAM_SCHEMA}-pixel-script`}
          dangerouslySetInnerHTML={{
            __html: `
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '648598344549426');
                fbq('track', 'PageView');
                window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                  try{
                    const { cartRes, checkoutRes, passes } = e.detail;
  
                    let content_name = "";
                    if(cartRes.products && cartRes.products.length) {
                      content_name = "${TEAM_SCHEMA}_single_game_purchases";
                    };
                    if(cartRes.passes && cartRes.passes.length && passes.length ) {
                      const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                      content_name = passDetail.passTitle;
                    };
  
                    fbq('track', 'Purchase', {
                      content_name: content_name, 
                      value: String(cartRes.totalAmount), 
                      currency: 'USD', 
                      order_id: checkoutRes.orderNumber,
                    });
                    console.log('meta pixel purchase event fired.');
                  
                  } catch(error){
                    console.log('meta pixel purchase event error:', error); 
                  }
                });
              `,
          }}
        />
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=648598344549426&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>

      <Script
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        type="text/javascript"
        src="https://js.adsrvr.org/up_loader.1.1.0.js"
        onLoad={() => {
          console.log("TTD base library loaded");
        }}
        onError={(e) => {
          console.error("Failed to load TTD base library:", e);
        }}
      />

      <Script
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        type="text/javascript"
        id={`${TEAM_SCHEMA}-ttd-script`}
        dangerouslySetInnerHTML={{
          __html: `
            function initTTDPixel() {
              if (typeof TTDUniversalPixelApi === 'function') {
                var universalPixelApi = new TTDUniversalPixelApi();
                window.addEventListener("${constants.customEvents.purchaseSuccess}", function (e) {
                  try {
                    const { cartRes, checkoutRes, passes } = e.detail;

                    let quantity = "";

                    if (cartRes.passes && cartRes.passes.length && passes.length) {
                      const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                      quantity = passDetail.seatCount;
                    }

                    universalPixelApi.init("ihkrrn1", ["kkn2yn4"], "https://insight.adsrvr.org/track/up", {
                      td1: quantity,
                      td2: checkoutRes.orderNumber,
                      v: String(cartRes.totalAmount),
                    });

                    console.log("ttd pixel event fired");
                  } catch (error) {
                    console.log("ttd pixel event error:", error);
                  }
                });
              } else {
                console.log("TTDUniversalPixelApi not found, retrying in 1 second");
                setTimeout(initTTDPixel, 1000);
              }
            }

            // Wait for DOM to be ready
            if (document.readyState === 'loading') {
              document.addEventListener('DOMContentLoaded', initTTDPixel);
            } else {
              initTTDPixel();
            }
          `,
        }}
      />
    </>
  );
};

export const FOOTER_LINKS = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() =>
          openPdfInNewTab(global_config.TERMS_AND_CONDITIONS_PDF_URL)
        }
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        Terms & Conditions
      </SSText>
      <SSText
        variant="body"
        onClick={() => openPdfInNewTab(global_config.PRIVACY_POLICY_PDF_URL)}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        Privacy Policy
      </SSText>
    </>
  );
};
